/** Row accessor: reads a column value from a row. */
export type Accessor<R> = (row: R, key: string) => unknown;

const defaultAccessor = <R>(row: R, key: string): unknown => (row as Record<string, unknown>)[key];

export interface SortSpec {
  key: string;
  dir: 'asc' | 'desc';
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

/** Compares cell values: numbers numerically, booleans false < true, text naturally; blanks last. */
export function compareValues(a: unknown, b: unknown): number {
  if (isBlank(a) || isBlank(b)) return Number(isBlank(a)) - Number(isBlank(b));
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/** Stable multi-column sort. Blank cells stay last in both directions. */
export function sortRows<R>(
  rows: readonly R[],
  sorts: readonly SortSpec[],
  get: Accessor<R> = defaultAccessor,
): R[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((x, y) => {
      for (const s of sorts) {
        const a = get(x.row, s.key);
        const b = get(y.row, s.key);
        const blank = Number(isBlank(a)) - Number(isBlank(b));
        const c = blank !== 0 ? blank : compareValues(a, b) * (s.dir === 'asc' ? 1 : -1);
        if (c !== 0) return c;
      }
      return x.index - y.index;
    })
    .map((x) => x.row);
}

/** Text a filter matches against: booleans as `true`/`false`, blanks as empty. */
export function cellText(v: unknown): string {
  return isBlank(v) ? '' : String(v);
}

/** Keeps rows where every non-empty filter is contained (case-insensitive) in its column. */
export function filterRows<R>(
  rows: readonly R[],
  filters: Readonly<Record<string, string>>,
  get: Accessor<R> = defaultAccessor,
): R[] {
  const active = Object.entries(filters)
    .map(([key, q]) => [key, q.trim().toLowerCase()] as const)
    .filter(([, q]) => q !== '');
  return rows.filter((row) =>
    active.every(([key, q]) => cellText(get(row, key)).toLowerCase().includes(q)),
  );
}

export interface RowGroup<R> {
  /** Group label (cell text; empty for blanks). */
  key: string;
  rows: R[];
}

/** Groups rows by a column, groups in order of first appearance. */
export function groupRows<R>(
  rows: readonly R[],
  key: string,
  get: Accessor<R> = defaultAccessor,
): RowGroup<R>[] {
  const groups = new Map<string, R[]>();
  for (const row of rows) {
    const k = cellText(get(row, key));
    groups.set(k, [...(groups.get(k) ?? []), row]);
  }
  return [...groups.entries()].map(([k, rs]) => ({ key: k, rows: rs }));
}
